import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { BsCloudUpload } from "react-icons/bs";
import PopupModal from "../../PopupModal";
import {
  StyledInput,
  StyledInputContainer,
  StyledInputGroup,
  StyledLabel,
  StyledSelect,
  StyledSelectContainer,
  StyledSelectGroup,
} from "../../../StyledCss/styledInputs";
import { PrimaryButton, SecondaryButton } from "../../../StyledCss/styledbtn";
import { ErrorText } from "../../../StyledCss/styledComponents";
import { createHiringApplication } from "../../../api/services/hroneApi";
import {
  EMAIL_PATTERN,
  MOBILE_PATTERN,
  isPdfResumeFile,
  validateTextField,
  validateNumericField,
} from "../../../util/hiringUtils";
import "../AddCandidateModal/index.css";
import "./index.css";

const RESUME_ACCEPT = "application/pdf,.pdf";
const RESUME_INPUT_ID = "refer-candidate-resume";

const DEFAULT_VALUES = {
  firstName: "",
  lastName: "",
  email: "",
  mobile: "",
  experienceType: "EXPERIENCED",
  totalExperience: "",
};

const ReferCandidateModal = ({ jobId, isOpen, onClose, onSuccess }) => {
  const orgDetails = JSON.parse(localStorage.getItem("org"));
  const primaryColor = orgDetails?.primaryColor || "#e86020";

  const [resumeFile, setResumeFile] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    setError,
    clearErrors,
    formState: { errors },
  } = useForm({ defaultValues: DEFAULT_VALUES });

  const experienceType = watch("experienceType");
  const isFresher = experienceType === "FRESHER";

  useEffect(() => {
    if (!isOpen) return;
    reset(DEFAULT_VALUES);
    setResumeFile(null);
    const inp = document.getElementById(RESUME_INPUT_ID);
    if (inp) inp.value = "";
  }, [isOpen, reset]);

  useEffect(() => {
    if (!isFresher) return;
    setValue("totalExperience", "");
    clearErrors("totalExperience");
  }, [isFresher, setValue, clearErrors]);

  const handleResumeChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!isPdfResumeFile(file)) {
      setError("resume", { type: "manual", message: "Only PDF files are allowed" });
      setResumeFile(null);
      e.target.value = "";
      return;
    }
    clearErrors("resume");
    setResumeFile(file);
  };

  const clearResume = () => {
    setResumeFile(null);
    clearErrors("resume");
    const inp = document.getElementById(RESUME_INPUT_ID);
    if (inp) inp.value = "";
  };

  const openResumePicker = () => document.getElementById(RESUME_INPUT_ID)?.click();

  const handleClose = () => {
    if (isSubmitting) return;
    onClose();
  };

  const onSubmit = handleSubmit(async (data) => {
    if (!resumeFile) {
      setError("resume", { type: "manual", message: "Please attach the candidate's resume" });
      return;
    }

    const fd = new FormData();
    fd.append("first_name", data.firstName.trim());
    fd.append("last_name", data.lastName.trim());
    fd.append("email", data.email.trim());
    fd.append("mobile", data.mobile.trim());
    fd.append("experience_type", data.experienceType);
    if (data.experienceType !== "FRESHER" && data.totalExperience) {
      fd.append("total_experience", data.totalExperience);
    }
    fd.append("status", "APPLIED");
    fd.append("application_source", "Referral");
    fd.append("referral", "true");
    fd.append("resume", resumeFile, resumeFile.name);

    setIsSubmitting(true);
    try {
      const res = await createHiringApplication(jobId, fd);
      const body = await res.json();
      if (!body.status) throw new Error(body.detail || body.message || "Failed to submit referral");
      toast.success("Candidate referred successfully");
      onSuccess?.(body.data);
    } catch (err) {
      toast.error(err.message || "Something went wrong. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  });

  const errBorder = (field) => (errors[field] ? { borderLeft: "10px solid red" } : {});

  return (
    <PopupModal showModal={isOpen} modalName="Refer a Candidate" closeBtn={handleClose} width="600px">
      <form
        className="refer-candidate-form"
        onSubmit={onSubmit}
        autoComplete="off"
        noValidate
        style={{ "--hiring-primary": primaryColor }}
      >
        <div className="refer-candidate-grid">
          <StyledInputGroup>
            <StyledInputContainer>
              <StyledInput
                placeholder=" "
                style={{ width: "100%", ...errBorder("firstName") }}
                {...register("firstName", {
                  validate: (v) => validateTextField(v, "First name", { required: true }) || true,
                })}
              />
              <StyledLabel className="user-label">First Name *</StyledLabel>
            </StyledInputContainer>
            {errors.firstName && <ErrorText>{errors.firstName.message}</ErrorText>}
          </StyledInputGroup>

          <StyledInputGroup>
            <StyledInputContainer>
              <StyledInput
                placeholder=" "
                style={{ width: "100%", ...errBorder("lastName") }}
                {...register("lastName", {
                  validate: (v) => validateTextField(v, "Last name", { required: true }) || true,
                })}
              />
              <StyledLabel className="user-label">Last Name *</StyledLabel>
            </StyledInputContainer>
            {errors.lastName && <ErrorText>{errors.lastName.message}</ErrorText>}
          </StyledInputGroup>

          <StyledInputGroup>
            <StyledInputContainer>
              <StyledInput
                type="email"
                placeholder=" "
                style={{ width: "100%", ...errBorder("email") }}
                {...register("email", {
                  required: true,
                  pattern: { value: EMAIL_PATTERN, message: "Enter a valid email address" },
                })}
              />
              <StyledLabel className="user-label">Candidate Email *</StyledLabel>
            </StyledInputContainer>
            {errors.email && <ErrorText>{errors.email.message || "Email is required"}</ErrorText>}
          </StyledInputGroup>

          <StyledInputGroup>
            <StyledInputContainer>
              <StyledInput
                placeholder=" "
                style={{ width: "100%", ...errBorder("mobile") }}
                {...register("mobile", {
                  required: true,
                  pattern: { value: MOBILE_PATTERN, message: "Enter a valid mobile number" },
                })}
              />
              <StyledLabel className="user-label">Candidate Phone *</StyledLabel>
            </StyledInputContainer>
            {errors.mobile && <ErrorText>{errors.mobile.message || "Mobile number is required"}</ErrorText>}
          </StyledInputGroup>

          <StyledSelectGroup>
            <StyledSelectContainer>
              <StyledSelect width="100%" {...register("experienceType")}>
                <option value="EXPERIENCED">Experienced</option>
                <option value="FRESHER">Fresher</option>
              </StyledSelect>
              <StyledLabel hasValue className="user-label">Candidate Type</StyledLabel>
            </StyledSelectContainer>
          </StyledSelectGroup>

          {!isFresher && (
            <StyledInputGroup>
              <StyledInputContainer>
                <StyledInput
                  type="number"
                  min={0}
                  step="0.1"
                  placeholder=" "
                  style={{ width: "100%", ...errBorder("totalExperience") }}
                  {...register("totalExperience", {
                    validate: (v) => validateNumericField(v, "Total experience", { required: true }) || true,
                  })}
                />
                <StyledLabel className="user-label" style={{ whiteSpace: "nowrap" }}>
                  Total Experience (Years) *
                </StyledLabel>
              </StyledInputContainer>
              {errors.totalExperience && <ErrorText>{errors.totalExperience.message}</ErrorText>}
            </StyledInputGroup>
          )}

          <div className="refer-candidate-field-full">
            <div className="add-candidate-resume-field">
              <div
                className="add-candidate-file-picker"
                onClick={openResumePicker}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => e.key === "Enter" && openResumePicker()}
              >
                <input
                  id={RESUME_INPUT_ID}
                  type="file"
                  accept={RESUME_ACCEPT}
                  style={{ display: "none" }}
                  onChange={handleResumeChange}
                />
                <BsCloudUpload
                  style={{ fontSize: "15px", color: resumeFile ? primaryColor : "#9ca3af", flexShrink: 0 }}
                />
                <span className={resumeFile ? "add-candidate-file-name" : "add-candidate-file-placeholder"}>
                  {resumeFile ? resumeFile.name : "Click to attach resume (PDF only) *"}
                </span>
                {resumeFile && (
                  <button
                    type="button"
                    className="add-candidate-file-clear-btn"
                    title="Remove file"
                    onClick={(e) => { e.stopPropagation(); clearResume(); }}
                  >
                    ×
                  </button>
                )}
              </div>
              {errors.resume && <ErrorText>{errors.resume.message}</ErrorText>}
            </div>
          </div>
        </div>

        <div className="refer-candidate-actions">
          <SecondaryButton type="button" onClick={handleClose} disabled={isSubmitting}>Cancel</SecondaryButton>
          <PrimaryButton type="submit" disabled={isSubmitting}>
            {isSubmitting ? "Submitting…" : "Submit Referral"}
          </PrimaryButton>
        </div>
      </form>
    </PopupModal>
  );
};

export default ReferCandidateModal;
